<script setup lang="ts">
import { getSeoDesc } from '@/utils/getSeoDescription'

import { useCityStore } from '@/store/city/city'

import { HttpStatusCode, NotFoundError, ServerError } from '@/api/results'

import AppSeoDescription from '@/components/App/AppSeoDescription.vue'
import CategoryEmpty from '@/components/Category/CategoryEmpty.vue'
import CategoryPage from '@/components/Category/CategoryPage.vue'

const { isDesktop } = useResponsive()

const route = useRoute()

const cityStore = useCityStore()

const { domain } = cityStore.cityInfo

usePopupFetch()

const {
  data: currentCategory,
  pending,
  error,
} = await useAsyncData(async (nuxtApp) => {
  const slug = String(route.params.category)

  const result = await nuxtApp?.$apiService.category.fetchOneCategory(slug)

  if (result instanceof NotFoundError) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  return result?.data
})

if (error.value) showError(error.value)

const emptyPageShow = computed(
  () => currentCategory.value?.products.length === 0,
)

const seoDesc = computed(
  () =>
    currentCategory.value?.seo?.seo_text ||
    getSeoDesc(
      route.params?.category,
      cityStore.cityInfo?.padeg?.pred || '',
      cityStore.cityInfo.phone,
    ),
)
</script>

<template>
  <div class="category-page" :class="{ 'category-page--loading': pending }">
    <app-head
      :title="currentCategory?.seo?.seo_title"
      :seo="{
        title: currentCategory?.seo?.seo_title,
        description: currentCategory?.seo?.seo_description,
        keywords: currentCategory?.seo?.seo_keywords,
      }"
    />

    <the-breadcrumbs
      v-if="currentCategory?.breadcrumbs"
      class="category-page__breadcrumbs"
      :breadcrumbs="currentCategory.breadcrumbs"
      :domain="domain"
    />

    <h1 v-if="currentCategory?.name" class="category-page__headline">
      {{ currentCategory?.seo?.seo_h1 }}
    </h1>

    <div
      v-if="isDesktop && currentCategory?.description"
      class="category-page__description"
      v-html="currentCategory?.description"
    />

    <category-empty v-if="emptyPageShow" />

    <category-page
      v-else-if="currentCategory"
      :current-category="currentCategory"
      :is-loading="pending"
    />

    <!--
      Здесь условие на pending затем, чтобы перерисовка
      между состояниями (смены категорий) не была такая заметная
    -->
    <app-seo-description
      v-if="seoDesc && !pending"
      class="category-page__seo"
      :seo-content="seoDesc"
    />
  </div>
</template>

<style lang="scss" scoped>
.category-page {
  &--loading {
    pointer-events: none;
    transition: all 3s cubic-bezier(0.075, 0.82, 0.165, 1);
  }

  @include media('<tablet') {
    padding: 20px 10px 30px;
  }

  &__breadcrumbs {
    padding-bottom: 20px;
  }

  &__headline {
    margin-bottom: 30px;
    font-size: 28px;
    font-weight: 900;

    @include media('<=tablet') {
      margin-bottom: 0;
      font-size: 18px;
      line-height: 21px;
    }
  }

  &__description {
    margin: 30px 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    color: var(--ui-text-primary);
  }

  &__seo {
    margin-top: 100px;

    @include media('<desktop-mini') {
      margin-top: 40px;
      margin-bottom: 0;
    }
  }
}
</style>
